import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getPriceTypeLabel, localStorageKeys, permissionLevelEnum, systemConfigKeyEnum } from '../../../constants';
import { useMachinesQuery } from '../../../hooks/queries/useMachinesQuery';
import { useAuth } from '../../../hooks/useAuth';
import { useSystemConfig } from '../../../hooks/useSystemConfig';
import { Machine } from '../../../services/apiSLTP/materials/machines';
import { capitalizeFirstLetter, formatName } from '../../../utils/capitalize';
import { formatNumber } from '../../../utils/numbers';
import { InheritedPriceDisplay } from '../../domain/InheritedPriceDisplay';
import GenericAgGridTable from '../GenericAgGridTable';

interface MachinesPLITableProps {}

const MachinesPLITable: React.FC<MachinesPLITableProps> = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const { data: dataMachines, isLoading: isMachinesLoading } = useMachinesQuery({ queries: { active: true } });

    const sortedMachines = () =>
        dataMachines?.sort((a, b) => {
            const typeA = a.machineTypeId?.displayOrder || 0;
            const typeB = b.machineTypeId?.displayOrder || 0;
            if (typeA < typeB) return -1;
            if (typeA > typeB) return 1;
            const designationA = a?.designation ?? '';
            const designationB = b?.designation ?? '';
            return designationA.localeCompare(designationB);
        }) || [];

    const columnDefs: ColDef<Machine>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('price') + ` (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`),
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            valueGetter: (params) => params.data?.price ?? params.data?.machineTypeId?.price ?? null,
            cellRenderer: (params: ICellRendererParams<Machine>) =>
                params.data ? (
                    <InheritedPriceDisplay
                        directValue={params.data.price}
                        inheritedValue={params.data.machineTypeId?.price}
                        format={(value) => formatNumber(value, t('locale'))}
                    />
                ) : (
                    params.value
                ),
            colId: 'price',
        },
        {
            headerName: capitalizeFirstLetter(t('pricing')),
            valueGetter: (params) => (params.data ? t(getPriceTypeLabel(params.data.priceType ?? params.data.machineTypeId?.priceType)) : ''),
            cellRenderer: (params: ICellRendererParams<Machine>) =>
                params.data ? (
                    <InheritedPriceDisplay
                        directValue={params.data.priceType}
                        inheritedValue={params.data.machineTypeId?.priceType}
                        format={(value) => capitalizeFirstLetter(t(getPriceTypeLabel(value)))}
                    />
                ) : (
                    params.value
                ),
            colId: 'pricing',
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'codeProC',
            colId: 'codeProC',
        },
        {
            headerName: capitalizeFirstLetter(t('type')),
            field: 'machineTypeId.designation',
            colId: 'type',
        },
        {
            headerName: capitalizeFirstLetter(t('driver')),
            field: 'userId.firstName',
            valueGetter: (params) => formatName(params.data?.userId?.firstName, params.data?.userId?.lastName),
            colId: 'driver',
        },
        ...(user?.profileId.permsLevel === permissionLevelEnum.ADMIN
            ? ([
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                      minWidth: 250,
                      colId: '_id',
                  },
              ] as ColDef<Machine>[])
            : []),
    ];

    return (
        <GenericAgGridTable
            rowData={sortedMachines()}
            columnDefs={columnDefs}
            loading={isMachinesLoading}
            exportCsvFileName="sales-pli-machines"
            storageKey={localStorageKeys.TABLE.PLI.MACHINES}
            agGridProps={{ getRowId: ({ data }) => data._id }}
        />
    );
};

export default MachinesPLITable;
